﻿import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CartItem, SavedItem } from '@/types';

interface CartState {
  items: CartItem[];
  savedItems: SavedItem[];
  couponCode: string;
  couponDiscount: number;
  isLoading: boolean;
}

const initialState: CartState = {
  items: [],
  savedItems: [],
  couponCode: '',
  couponDiscount: 0,
  isLoading: false,
};

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setItems: (state, action: PayloadAction<CartItem[]>) => {
      state.items = action.payload;
    },
    addItem: (state, action: PayloadAction<CartItem>) => {
      const existing = state.items.find(
        (item) =>
          item.product.id === action.payload.product.id &&
          item.selectedSize === action.payload.selectedSize &&
          item.selectedColor === action.payload.selectedColor,
      );
      if (existing) {
        existing.id = action.payload.id;
        existing.quantity = action.payload.quantity;
        existing.variant = action.payload.variant;
        existing.addedAt = action.payload.addedAt;
      } else {
        state.items.push(action.payload);
      }
    },
    removeItem: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
    },
    updateQuantity: (state, action: PayloadAction<{ id: string; quantity: number }>) => {
      const item = state.items.find((i) => i.id === action.payload.id);
      if (item) {
        item.quantity = Math.max(1, action.payload.quantity);
      }
    },
    clearCart: (state) => {
      state.items = [];
      state.couponCode = '';
      state.couponDiscount = 0;
    },
    saveForLater: (state, action: PayloadAction<string>) => {
      const itemIndex = state.items.findIndex((i) => i.id === action.payload);
      if (itemIndex !== -1) {
        const cartItem = state.items[itemIndex];
        state.savedItems.push({
          id: cartItem.id,
          product: cartItem.product,
          savedAt: new Date().toISOString(),
        });
        state.items.splice(itemIndex, 1);
      }
    },
    moveToCart: (state, action: PayloadAction<string>) => {
      const savedIndex = state.savedItems.findIndex((i) => i.id === action.payload);
      if (savedIndex !== -1) {
        const savedItem = state.savedItems[savedIndex];
        state.items.push({
          id: savedItem.id,
          product: savedItem.product,
          quantity: 1,
          selectedSize: savedItem.product.sizes[0]?.value ?? '',
          selectedColor: savedItem.product.colors[0]?.name ?? '',
          addedAt: new Date().toISOString(),
        });
        state.savedItems.splice(savedIndex, 1);
      }
    },
    removeSavedItem: (state, action: PayloadAction<string>) => {
      state.savedItems = state.savedItems.filter((i) => i.id !== action.payload);
    },
    applyCoupon: (state, action: PayloadAction<{ code: string; discount: number }>) => {
      state.couponCode = action.payload.code;
      state.couponDiscount = action.payload.discount;
    },
    removeCoupon: (state) => {
      state.couponCode = '';
      state.couponDiscount = 0;
    },
  },
});

export const cartActions = cartSlice.actions;

